import React, { useCallback, useEffect, useMemo, useState } from "react";

import Button from "../../Components/Button";
import CustomCheckbox from "../../Components/CheckCircle";
import CommonInput from "../../Components/Input";
import Select from "../../Components/Select";
import StepProgressBar from "../../Components/StepProgressBar";
import TabsComponent from "../../Components/Tab";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchOrganizationLevels } from "../../Store/Reducers/AdminSlice";
import {
  CreateTemplateRequest,
  Question,
  QuestionOption,
} from "../../Store/Reducers/AssessmentSlice";
import "./style.scss";

/* ---------------------------- Constants & Helpers --------------------------- */

const STRATEGIC_CATEGORY = "Strategic Conversations";

const getStrategicConversationsQuestions = (
  organizationLevels: Array<{ level_code: string; name: string }>
): Question[] => {
  const maturityLevelOptions: QuestionOption[] = organizationLevels.map(
    level => ({
      value: level.level_code,
      label: level.name,
    })
  );

  return [
    {
      id: "maturity_level",
      question: "What do you think on Current level of Customer?",
      questionType: "single_choice",
      required: true,
      options: maturityLevelOptions,
    },
    {
      question: "What is your Rational for it ?",
      questionType: "free_text",
      required: true,
      options: [],
    },
  ];
};

const getDefaultCategoryQuestions = (
  category: string,
  organizationLevels: Array<{ level_code: string; name: string }> = []
): Question[] => getStrategicConversationsQuestions(organizationLevels);

const cloneQuestionSet = (questions: Question[]): Question[] =>
  questions.map(q => ({
    ...q,
    options:
      q.questionType === "free_text"
        ? []
        : (q.options ?? []).map(o => ({ ...o })),
  }));

const generateId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? (crypto as any).randomUUID()
    : Math.random().toString(36).substring(2, 11);

const parseErrorMessages = (message: string | null): string[] => {
  if (!message) return [];
  const unique = new Set<string>();
  const add = (v: unknown) => {
    if (!v) return;
    const s = String(v).trim();
    if (s) unique.add(s);
  };
  try {
    const parsed = JSON.parse(message);
    if (typeof parsed === "string") add(parsed);
    else if (Array.isArray(parsed)) parsed.forEach(add);
    else if (typeof parsed === "object" && parsed !== null) {
      if ("detail" in parsed) add((parsed as any).detail);
      if ("errors" in parsed && typeof (parsed as any).errors === "object") {
        Object.entries((parsed as any).errors).forEach(([k, v]) => {
          if (Array.isArray(v)) v.forEach(i => add(`${k}: ${i}`));
          else add(`${k}: ${v}`);
        });
      } else {
        Object.values(parsed).forEach(v => {
          if (Array.isArray(v)) v.forEach(add);
          else if (typeof v !== "object") add(v);
        });
      }
    }
  } catch {
    add(message);
  }
  return Array.from(unique);
};

/* ------------------------------- Small Types -------------------------------- */

type CustomOption = {
  id: string;
  value: string;
  label: string;
};

type CustomQuestionItem = {
  id: string;
  questionType: any;
  required: boolean;
  question: string;
  options: CustomOption[];
};

type CustomQuestionFormState = {
  questionType: any;
  required: boolean;
  question: string;
  options: CustomOption[];
};

type QuestionContext = { type: "small" } | { type: "large"; category: string };

/* ------------------------------- Icon Components ---------------------------- */

const ChevronIcon: React.FC<{ expanded?: boolean; onClick?: () => void }> = ({
  expanded,
  onClick,
}) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`chevron-arrow ${expanded ? "expanded" : "collapsed"}`}
    onClick={onClick}
    style={{ cursor: "pointer" }}
  >
    <path
      d="M7.41 8.59L12 13.17L16.59 8.59L18 10L12 16L6 10L7.41 8.59Z"
      fill="#5E6368"
    />
  </svg>
);

const EditIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M15.8805 5.10929C16.277 4.71285 16.4998 4.17514 16.4999 3.61443C16.4999 3.05372 16.2773 2.51594 15.8808 2.11941C15.4844 1.72288 14.9467 1.50007 14.386 1.5C13.8253 1.49993 13.2875 1.7226 12.891 2.11904L2.88145 12.1308C2.70732 12.3044 2.57854 12.5182 2.50645 12.7533L1.5157 16.0173C1.49632 16.0821 1.49485 16.151 1.51146 16.2167C1.52807 16.2823 1.56214 16.3422 1.61005 16.39C1.65796 16.4379 1.71792 16.4718 1.78357 16.4883C1.84922 16.5048 1.91812 16.5033 1.98295 16.4838L5.2477 15.4938C5.48258 15.4224 5.69633 15.2944 5.8702 15.121L15.8805 5.10929Z"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11.25 3.75L14.25 6.75"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DeleteIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.5 8.25V12.75"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.5 8.25V12.75"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14.25 4.5V15C14.25 15.3978 14.092 15.7794 13.8107 16.0607C13.5294 16.342 13.1478 16.5 12.75 16.5H5.25C4.85218 16.5 4.47064 16.342 4.18934 16.0607C3.90804 15.7794 3.75 15.3978 3.75 15V4.5"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2.25 4.5H15.75"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 4.5V3C6 2.60218 6.15804 2.22064 6.43934 1.93934C6.72064 1.65804 7.10218 1.5 7.5 1.5H10.5C10.8978 1.5 11.2794 1.65804 11.5607 1.93934C11.842 2.22064 12 2.60218 12 3V4.5"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MandatoryIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.0833 6.42188H2.91667C2.27233 6.42188 1.75 6.94421 1.75 7.58854V11.6719C1.75 12.3162 2.27233 12.8385 2.91667 12.8385H11.0833C11.7277 12.8385 12.25 12.3162 12.25 11.6719V7.58854C12.25 6.94421 11.7277 6.42188 11.0833 6.42188Z"
      stroke="#565656"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.08594 6.42187V4.08854C4.08594 3.31499 4.39323 2.57313 4.94021 2.02615C5.48719 1.47917 6.22906 1.17188 7.0026 1.17188C7.77615 1.17188 8.51802 1.47917 9.065 2.02615C9.61198 2.57313 9.91927 3.31499 9.91927 4.08854V6.42187"
      stroke="#565656"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ------------------------------ Available Data ------------------------------ */

const questionTypeOptions = [
  {
    label: "Single Choice",
    value: "single_choice",
  },
  {
    label: "Multiple Choice",
    value: "multiple_choice",
  },
  {
    label: "Free Text",
    value: "free_text",
  },
  {
    label: "Scale",
    value: "scale",
  },
];

const availableCategories = [
  { label: "Strategic Conversations", value: "Strategic Conversations" },
  { label: "Regular Engagement", value: "Regular Engagement" },
  { label: "Trust and candid Dialogue", value: "Trust and candid Dialogue" },
  { label: "Two-Way Value Recognition", value: "Two-Way Value Recognition" },
  { label: "Customer Success", value: "Customer Success" },
  { label: "Technical Support", value: "Technical Support" },
  { label: "Business Development", value: "Business Development" },
];

/* ------------------------------ Main Component ------------------------------ */

const MAX_CUSTOM_OPTIONS = 10;
const MIN_CUSTOM_OPTIONS = 2;

const createEmptyOption = (): CustomOption => ({
  id: generateId(),
  value: "",
  label: "",
});

const createInitialCustomQuestion = (): CustomQuestionFormState => ({
  questionType: "Single Choice",
  required: true,
  question: "",
  options: [createEmptyOption(), createEmptyOption()],
});

const CreateAssessmentTemplateContainer: React.FC<{
  onBack?: () => void;
  onSubmit: (payload: CreateTemplateRequest) => Promise<void>;
  isSubmitting?: boolean;
}> = ({ onBack, onSubmit, isSubmitting = false }) => {
  // Redux hooks
  const dispatch = useAppDispatch();
  const { organizationLevels } = useAppSelector((state: any) => state.admin);

  // Fetch organization levels on mount
  useEffect(() => {
    dispatch(fetchOrganizationLevels());
  }, [dispatch]);

  // ui & form
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [formData, setFormData] = useState<{
    templateName: string;
    description: string;
  }>({ templateName: "", description: "" });
  const [selectedQuestionnaireType, setSelectedQuestionnaireType] = useState<
    "short" | "long"
  >("short");

  // dynamic category/question state
  const [customQuestion, setCustomQuestion] = useState<CustomQuestionFormState>(
    () => createInitialCustomQuestion()
  );
  const [customQuestions, setCustomQuestions] = useState<{
    small: CustomQuestionItem[];
    large: Record<string, CustomQuestionItem[]>;
  }>({ small: [], large: {} });
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(
    null
  );
  const [editingContext, setEditingContext] = useState<QuestionContext | null>(
    null
  );

  const [isQuestionsExpanded, setIsQuestionsExpanded] = useState<boolean>(true);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});
  const [selectedCategoryValue, setSelectedCategoryValue] =
    useState<string>("");

  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [templateNameError, setTemplateNameError] = useState<string | null>(
    null
  );

  /* --------------------------- Memoized Derived Data --------------------------- */

  const steps = useMemo(
    () => [
      { label: "Scope" },
      { label: "Select Categories" },
      { label: "Preview" },
    ],
    []
  );

  // Memoized default category questions with dynamic organization levels
  const defaultCategoryQuestions = useMemo(
    () =>
      getDefaultCategoryQuestions(STRATEGIC_CATEGORY, organizationLevels || []),
    [organizationLevels]
  );

  const SMALL_STATIC_QUESTIONS_COUNT = useMemo(
    () => defaultCategoryQuestions.length,
    [defaultCategoryQuestions]
  );

  const smallAssessmentQuestionCount = useMemo(
    () => SMALL_STATIC_QUESTIONS_COUNT + customQuestions.small.length,
    [SMALL_STATIC_QUESTIONS_COUNT, customQuestions.small.length]
  );

  const getTotalQuestionsForCategory = useCallback(
    (category: string) => customQuestions.large[category]?.length ?? 0,
    [customQuestions.large]
  );

  const largeAssessmentQuestionCount = useMemo(
    () =>
      selectedCategories.reduce(
        (total, cat) => total + getTotalQuestionsForCategory(cat),
        0
      ),
    [selectedCategories, getTotalQuestionsForCategory]
  );

  const errorMessages = useMemo(
    () => parseErrorMessages(submissionError),
    [submissionError]
  );

  /* ------------------------------ Handlers ---------------------------------- */

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
      if (name === "templateName" && templateNameError)
        setTemplateNameError(null);
    },
    [templateNameError]
  );

  const handleCustomQuestionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCustomQuestion(prev => ({ ...prev, question: e.target.value }));
    },
    []
  );

  const handleQuestionTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextType = e.target
        .value as CustomQuestionFormState["questionType"];
      setCustomQuestion(prev => ({
        ...prev,
        questionType: nextType,
        options:
          nextType === "free_text"
            ? [createEmptyOption(), createEmptyOption()]
            : prev.options.length
              ? prev.options
              : [createEmptyOption(), createEmptyOption()],
      }));
    },
    []
  );

  const addOption = useCallback(() => {
    setCustomQuestion(prev => {
      if (prev.options.length >= MAX_CUSTOM_OPTIONS) return prev;
      return { ...prev, options: [...prev.options, createEmptyOption()] };
    });
  }, []);

  const removeOption = useCallback((index: number) => {
    setCustomQuestion(prev => {
      if (prev.options.length <= MIN_CUSTOM_OPTIONS) return prev;
      return { ...prev, options: prev.options.filter((_, i) => i !== index) };
    });
  }, []);

  const handleOptionChange = useCallback(
    (index: number, field: "value" | "label") =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setCustomQuestion(prev => ({
          ...prev,
          options: prev.options.map((opt, i) =>
            i === index ? { ...opt, [field]: value } : opt
          ),
        }));
      },
    []
  );

  const resetCustomQuestionForm = useCallback(() => {
    setCustomQuestion(createInitialCustomQuestion());
    setEditingQuestionId(null);
    setEditingContext(null);
  }, []);

  const isSameContext = useCallback(
    (next: QuestionContext, current: QuestionContext | null) => {
      if (!current) return false;
      if (next.type !== current.type) return false;
      if (next.type === "large")
        return next.category === (current as any).category;
      return true;
    },
    []
  );

  const handleAddCustomQuestion = useCallback(
    (context: QuestionContext) => {
      const trimmedQuestion = customQuestion.question.trim();
      const requiresOptions = customQuestion.questionType !== "free_text";
      const sanitizedOptions = customQuestion.options
        .map(o => ({ ...o, value: o.value.trim(), label: o.label.trim() }))
        .filter(o => o.value && o.label);

      if (!trimmedQuestion) return;
      if (requiresOptions && sanitizedOptions.length < MIN_CUSTOM_OPTIONS)
        return;

      const contextForComparison =
        context.type === "small"
          ? { type: "small" as const }
          : { type: "large" as const, category: context.category };
      const editingMatchesContext =
        editingQuestionId !== null &&
        isSameContext(contextForComparison, editingContext);

      const questionId =
        editingMatchesContext && editingQuestionId
          ? editingQuestionId
          : generateId();

      const newQuestion: CustomQuestionItem = {
        id: questionId,
        questionType: customQuestion.questionType,
        required: customQuestion.required,
        question: trimmedQuestion,
        options: requiresOptions ? sanitizedOptions : [],
      };

      setCustomQuestions(prev => {
        if (context.type === "small") {
          const updated = editingMatchesContext
            ? prev.small.map(q => (q.id === newQuestion.id ? newQuestion : q))
            : [...prev.small, newQuestion];
          return { ...prev, small: updated };
        } else {
          const existing = prev.large[context.category] ?? [];
          const updated = editingMatchesContext
            ? existing.map(q => (q.id === newQuestion.id ? newQuestion : q))
            : [...existing, newQuestion];
          return {
            ...prev,
            large: { ...prev.large, [context.category]: updated },
          };
        }
      });

      resetCustomQuestionForm();
    },
    [
      customQuestion,
      editingContext,
      editingQuestionId,
      resetCustomQuestionForm,
      isSameContext,
    ]
  );

  const handleCancelCustomQuestion = useCallback(
    () => resetCustomQuestionForm(),
    [resetCustomQuestionForm]
  );

  const handleEditQuestion = useCallback(
    (questionId: string, context?: QuestionContext) => {
      if (!context) return;
      const source =
        context.type === "small"
          ? customQuestions.small
          : context.type === "large"
            ? (customQuestions.large[context.category] ?? [])
            : [];
      const q = source.find(s => s.id === questionId);
      if (!q) return;
      setEditingQuestionId(q.id);
      setEditingContext(context);
      setCustomQuestion({
        questionType: q.questionType,
        required: q.required,
        question: q.question,
        options:
          q.questionType === "free_text"
            ? [createEmptyOption(), createEmptyOption()]
            : q.options.map(o => ({
                id: generateId(),
                value: o.value,
                label: o.label,
              })),
      });
    },
    [customQuestions]
  );

  const handleDeleteQuestion = useCallback(
    (questionId: string, context?: QuestionContext) => {
      if (!context) return;
      setCustomQuestions(prev => {
        if (context.type === "small") {
          return {
            ...prev,
            small: prev.small.filter(q => q.id !== questionId),
          };
        } else {
          const existing = prev.large[context.category] ?? [];
          return {
            ...prev,
            large: {
              ...prev.large,
              [context.category]: existing.filter(q => q.id !== questionId),
            },
          };
        }
      });
      if (editingQuestionId === questionId) resetCustomQuestionForm();
    },
    [editingQuestionId, resetCustomQuestionForm]
  );

  const toggleCategoryExpansion = useCallback((categoryName: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName],
    }));
  }, []);

  const removeCategory = useCallback((categoryName: string) => {
    setSelectedCategories(prev => prev.filter(c => c !== categoryName));
    setExpandedCategories(prev => {
      const copy = { ...prev };
      delete copy[categoryName];
      return copy;
    });
  }, []);

  const handleCategoryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const categoryValue = e.target.value;
      setSelectedCategoryValue(categoryValue);
      if (categoryValue && !selectedCategories.includes(categoryValue)) {
        setSelectedCategories(prev => [...prev, categoryValue]);
        setExpandedCategories(prev => ({ ...prev, [categoryValue]: false }));
      }
    },
    [selectedCategories]
  );

  const toggleQuestionsExpansion = useCallback(
    () => setIsQuestionsExpanded(prev => !prev),
    []
  );

  /* ------------------------------ Submission ------------------------------- */

  const buildQuestionPayload = useCallback(
    (question: CustomQuestionItem): Question => ({
      question: question.question,
      questionType: question.questionType,
      required: question.required,
      options:
        question.questionType === "free_text"
          ? []
          : question.options.map(o => ({ value: o.value, label: o.label })),
    }),
    []
  );

  // Transform payload to backend format
  const transformTemplatePayload = (
    payload: CreateTemplateRequest,
    customQuestionsByCategory: Record<string, CustomQuestionItem[]>
  ): any => {
    // Transform questions
    const transformQuestion = (q: any, index: number) => {
      const baseQuestion: any = {
        id: q.id || `q${index + 1}`,
        order_index: q.display_order ?? index + 1,
        question_text: q.question,
        question_type: q.questionType,
        required: q.required,
      };

      if (q.questionType === "free_text") {
        baseQuestion.options = {
          min_length: 1,
          max_length: 500,
        };
      } else if (q.options && q.options.length > 0) {
        baseQuestion.options = {
          choices: q.options.map((opt: any) => ({
            label: opt.label,
            value: opt.value,
          })),
          allow_multiple: q.questionType === "Multiple Choice",
        };
      }

      return baseQuestion;
    };

    const transformedPayload: any = {
      name: payload.name,
      questionnaire_type: payload.questionnaire_type,
      description: payload.description || "",
      is_active: true,
    };

    if (payload.questionnaire_type === "short") {
      // Short assessment: questions in data.questions
      transformedPayload.data = {
        metadata: {
          categories: payload.categories || [],
        },
        questions: payload.questions.map((q, idx) => transformQuestion(q, idx)),
      };
    } else {
      // Long assessment: group questions by category in data.categories
      // Questions are created per category in order, so we track counts per category
      let currentIndex = 0;

      transformedPayload.data = {
        metadata: {
          categories: payload.categories || [],
        },
        categories: payload.categories
          .map(categoryName => {
            const questionCount =
              customQuestionsByCategory[categoryName]?.length ?? 0;
            const categoryQuestions = payload.questions.slice(
              currentIndex,
              currentIndex + questionCount
            );
            const categoryData = {
              category_name: categoryName,
              questions: categoryQuestions.map((q, qIdx) =>
                transformQuestion(q, currentIndex + qIdx)
              ),
            };
            currentIndex += questionCount;
            return categoryData;
          })
          .filter(cat => cat.questions.length > 0),
      };
    }

    return transformedPayload;
  };

  const handleSubmit = useCallback(async () => {
    setSubmissionError(null);
    if (isSubmitting) return;
    const trimmedName = formData.templateName.trim();
    if (!trimmedName) {
      setTemplateNameError("Template name is required.");
      return;
    }
    const categoriesPayload =
      selectedQuestionnaireType === "short"
        ? [STRATEGIC_CATEGORY]
        : selectedCategories;
    if (
      selectedQuestionnaireType === "long" &&
      categoriesPayload.length === 0
    ) {
      setSubmissionError("Select at least one assessment category.");
      return;
    }

    const defaultSmallQuestions = cloneQuestionSet(defaultCategoryQuestions);
    const uniqueCategories = Array.from(new Set(categoriesPayload));

    const questionsPayload: Question[] =
      selectedQuestionnaireType === "short"
        ? [
            ...defaultSmallQuestions,
            ...customQuestions.small.map(buildQuestionPayload),
          ]
        : uniqueCategories.flatMap(category => [
            ...(customQuestions.large[category] ?? []).map(
              buildQuestionPayload
            ),
          ]);

    const payload: CreateTemplateRequest = {
      name: trimmedName,
      description: formData.description.trim() || undefined,
      categories: uniqueCategories,
      questionnaire_type: selectedQuestionnaireType,
      questions: questionsPayload,
    };

    try {
      const transformedPayload = transformTemplatePayload(
        payload,
        customQuestions.large
      );
      await onSubmit(transformedPayload);
    } catch (err: any) {
      const message =
        typeof err === "string"
          ? err
          : err?.message || "Failed to create assessment template";
      setSubmissionError(message);
    }
  }, [
    formData,
    isSubmitting,
    onSubmit,
    selectedQuestionnaireType,
    selectedCategories,
    customQuestions,
    buildQuestionPayload,
    defaultCategoryQuestions,
  ]);

  const handleNext = useCallback(async () => {
    if (currentStep === 1) {
      const trimmedName = formData.templateName.trim();
      if (!trimmedName) {
        setTemplateNameError("Template name is required.");
        return;
      }
      setTemplateNameError(null);
    }
    if (currentStep < 3) setCurrentStep(prev => prev + 1);
    else await handleSubmit();
  }, [currentStep, formData.templateName, handleSubmit]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
    else onBack?.();
  }, [currentStep, onBack]);

  /* ------------------------------- Render Helpers ------------------------------ */

  const renderCustomQuestionForm = useCallback(
    (context: QuestionContext) => {
      const requiresOptions = customQuestion.questionType !== "free_text";
      const optionsFilled = requiresOptions
        ? customQuestion.options.every(
            opt => opt.value.trim() && opt.label.trim()
          )
        : true;
      const hasMinimumOptions = requiresOptions
        ? customQuestion.options.length >= MIN_CUSTOM_OPTIONS
        : true;
      const isQuestionFilled = customQuestion.question.trim().length > 0;
      const isAddDisabled =
        !isQuestionFilled || !optionsFilled || !hasMinimumOptions;
      const canAddMoreOptions =
        requiresOptions && customQuestion.options.length < MAX_CUSTOM_OPTIONS;
      const canRemoveOption =
        requiresOptions && customQuestion.options.length > MIN_CUSTOM_OPTIONS;

      const isEditingCurrentContext =
        editingQuestionId !== null && isSameContext(context, editingContext);

      return (
        <div className="custom-question-form">
          <div className="form-row">
            <div className="form-group select-group">
              <Select
                name="questionType"
                value={customQuestion.questionType}
                onChange={handleQuestionTypeChange}
                options={questionTypeOptions}
                required
                className="question-type-select"
              />
            </div>
            <div className="required-checkbox">
              <CustomCheckbox
                label="Required"
                type="square"
                checked={customQuestion.required}
                onChange={e =>
                  setCustomQuestion(prev => ({
                    ...prev,
                    required: e.target.checked,
                  }))
                }
              />
            </div>
          </div>

          <div className="form-group">
            <CommonInput
              label="Enter Question"
              name="question"
              placeholder="Enter Your Questions"
              value={customQuestion.question}
              onChange={handleCustomQuestionChange}
              required
            />
          </div>

          {requiresOptions && (
            <div className="options-section">
              <div className="options-header">
                <div className="options-title">
                  <h5>Options</h5>
                  <span className="options-count">
                    ({customQuestion.options.length}/{MAX_CUSTOM_OPTIONS})
                  </span>
                </div>
                <button
                  type="button"
                  className="add-option-btn"
                  onClick={addOption}
                  disabled={!canAddMoreOptions}
                >
                  + Add Option
                </button>
              </div>
              <div className="options-hint">At least two choices required</div>
              <div className="options-list">
                {customQuestion.options.map((option, index) => (
                  <div key={option.id} className="option-row">
                    <CommonInput
                      name={`option-value-${index}`}
                      value={option.value}
                      placeholder="Option value"
                      onChange={handleOptionChange(index, "value")}
                      className="option-input-field"
                    />
                    <CommonInput
                      name={`option-label-${index}`}
                      value={option.label}
                      placeholder="Option label"
                      onChange={handleOptionChange(index, "label")}
                      className="option-input-field"
                    />
                    <button
                      type="button"
                      className="remove-option-btn"
                      onClick={() => removeOption(index)}
                      disabled={!canRemoveOption}
                      aria-label="Remove option"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.6667 3.33337L3.33337 12.6667"
                          stroke="#FF3B30"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M3.33337 3.33337L12.6667 12.6667"
                          stroke="#FF3B30"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="form-actions">
            <Button
              variant="outline-primary"
              className="cancel-btn"
              onClick={handleCancelCustomQuestion}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="add-questions-btn"
              onClick={() => handleAddCustomQuestion(context)}
              disabled={isAddDisabled}
            >
              {isEditingCurrentContext ? "Update Question" : "+ Add Questions"}
            </Button>
          </div>
        </div>
      );
    },
    [
      customQuestion,
      editingContext,
      editingQuestionId,
      addOption,
      removeOption,
      handleQuestionTypeChange,
      handleCustomQuestionChange,
      handleCancelCustomQuestion,
      handleAddCustomQuestion,
      handleOptionChange,
      isSameContext,
    ]
  );

  /* ------------------------------- JSX Render ------------------------------- */

  return (
    <div className="template-creation-container">
      <div className="template-content">
        <div className="template-content-header">
          <h2 className="fs-16 fw-700">
            {currentStep === 1
              ? "Basic Information"
              : currentStep === 2
                ? "Select Assessment Categories"
                : "Preview"}
          </h2>
          <div className="my-4">
            <StepProgressBar steps={steps} currentStepIndex={currentStep} />
          </div>
        </div>

        {errorMessages.length > 0 && (
          <div className="submission-error-banner" role="alert">
            <ul>
              {errorMessages.map(m => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="template-form">
          {/* STEP 1 - Basic Info */}
          {currentStep === 1 && (
            <div className="form-section">
              <div className="form-group">
                <CommonInput
                  label="Template Name"
                  name="templateName"
                  value={formData.templateName}
                  onChange={handleInputChange}
                  placeholder="Enter template name"
                  required
                  error={templateNameError || undefined}
                />
              </div>
              <div className="form-group">
                <CommonInput
                  label="Description"
                  name="description"
                  type="textarea"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter description"
                />
              </div>
            </div>
          )}

          {/* STEP 2 - Select Categories */}
          {currentStep === 2 && (
            <div className="form-section">
              <div className="questionnaire-type-section">
                <h3>Select Questionnaire Type</h3>
                <div className="flex gap-3 mb-4">
                  {[
                    {
                      id: "small",
                      label: "Short Assessment",
                    },
                    {
                      id: "large",
                      label: "Long Assessment",
                    },
                  ].map(item => (
                    <TabsComponent
                      key={item.id}
                      tabs={[item]}
                      activeTab={selectedQuestionnaireType}
                      onTabChange={setSelectedQuestionnaireType}
                      variant={
                        selectedQuestionnaireType === item.id
                          ? "pill-filled"
                          : "pill-outline"
                      }
                    />
                  ))}
                </div>

                {/* SHORT ASSESSMENT */}
                {selectedQuestionnaireType === "short" && (
                  <div className="assessment-card">
                    <div className="assessment-card-header">
                      <div className="title-with-collapse">
                        <ChevronIcon
                          expanded={isQuestionsExpanded}
                          onClick={toggleQuestionsExpansion}
                        />
                        <div className="title-and-subheading">
                          <h4>Short Assessments</h4>
                          <p>
                            Evaluate the consistency and frequency of
                            executive-level touchpoints
                          </p>
                        </div>
                      </div>
                      <div className="assessment-meta">
                        <span className="meta-badge">
                          {smallAssessmentQuestionCount} Questions
                        </span>
                        <span className="meta-badge">2 mandatory</span>
                      </div>
                    </div>

                    {isQuestionsExpanded && (
                      <div className="questions-list">
                        {/* static questions */}
                        {defaultCategoryQuestions.map((q, i) => (
                          <div
                            key={`static-${i}`}
                            className={`question-item ${q.required ? "question-item-blue" : "question-item-white"}`}
                          >
                            <div className="question-header">
                              <span className="question-number">{`Q${i + 1}:`}</span>
                              <span className="question-text">
                                {q.question}
                                <span className="question-type-badge">
                                  {q.questionType}
                                </span>
                              </span>
                              {q.required && (
                                <div className="mandatory-badge">
                                  <MandatoryIcon />
                                  <span>Mandatory</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}

                        {/* custom small questions */}
                        {customQuestions.small.map((question, index) => (
                          <div
                            key={question.id}
                            className="question-item question-item-white custom-question-item"
                          >
                            <div className="question-header">
                              <span className="question-number">{`Q${SMALL_STATIC_QUESTIONS_COUNT + index + 1}:`}</span>
                              <span className="question-text">
                                {question.question}
                                <span className="question-type-badge">
                                  {question.questionType}
                                </span>
                              </span>
                              <div className="question-actions flex items-center gap-2">
                                <div
                                  className="action-btn"
                                  onClick={() =>
                                    handleEditQuestion(question.id, {
                                      type: "small",
                                    })
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  <EditIcon />
                                </div>
                                <svg
                                  width="1"
                                  height="19"
                                  viewBox="0 0 1 19"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <line
                                    x1="0.5"
                                    x2="0.5"
                                    y2="19"
                                    stroke="#E5E5E5"
                                  />
                                </svg>
                                <div
                                  className="action-btn"
                                  onClick={() =>
                                    handleDeleteQuestion(question.id, {
                                      type: "small",
                                    })
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  <DeleteIcon />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add Custom Question Container */}
                    {isQuestionsExpanded && (
                      <div className="add-custom-question-container">
                        <div className="custom-question-header">
                          <div className="plus-icon" />{" "}
                          <h4>Add Custom Question</h4>
                        </div>
                        <div className="custom-question-content">
                          <p>
                            Evaluate the consistency and frequency of
                            executive-level touchpoints
                          </p>
                          <p>Ans: 1) Yes 2) No</p>
                        </div>
                        {renderCustomQuestionForm({ type: "small" })}
                      </div>
                    )}
                  </div>
                )}

                {/* LARGE / CATEGORIES */}
                {selectedQuestionnaireType === "short" && (
                  <div className="large-assessment-section">
                    <div className="category-selection-container">
                      <h3>Select Assessment Categories</h3>
                      <div className="form-group">
                        <Select
                          label="Choose Categories"
                          name="categories"
                          value={selectedCategoryValue}
                          onChange={handleCategoryChange}
                          options={availableCategories.filter(
                            cat => !selectedCategories.includes(cat.value)
                          )}
                          placeholder="Select categories"
                          required
                          className="category-select"
                        />
                      </div>

                      {selectedCategories.length > 0 && (
                        <div className="selected-categories-section">
                          <div className="selected-categories-header">
                            <div className="checkmark-icon" />
                            <h4>Selected Assessment Categories</h4>
                          </div>
                          <div className="category-tags">
                            {selectedCategories.map(category => (
                              <div key={category} className="category-tag">
                                <span>{category}</span>
                                <button
                                  type="button"
                                  onClick={() => removeCategory(category)}
                                  className="remove-category-btn"
                                >
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                                      stroke="#974AE4"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="categories-questionnaire-section">
                      <h3>Categories Questionnaire & Customization</h3>
                      {selectedCategories.map(category => (
                        <div key={category} className="assessment-card">
                          <div className="assessment-card-header">
                            <div className="title-with-collapse">
                              <ChevronIcon
                                expanded={!!expandedCategories[category]}
                                onClick={() =>
                                  toggleCategoryExpansion(category)
                                }
                              />
                              <div className="title-and-subheading">
                                <h4>{category}</h4>
                                <p>
                                  Evaluate the consistency and frequency of
                                  executive-level touchpoints
                                </p>
                              </div>
                            </div>
                            <div className="assessment-meta">
                              <span className="meta-badge">
                                {getTotalQuestionsForCategory(category)}{" "}
                                Questions
                              </span>
                              <span className="meta-badge">2 mandatory</span>
                            </div>
                          </div>

                          {expandedCategories[category] && (
                            <div className="questions-list">
                              {/* Only show custom questions for category */}
                              {(customQuestions.large[category] ?? []).map(
                                (question, idx) => (
                                  <div
                                    key={question.id}
                                    className="question-item question-item-white custom-question-item"
                                  >
                                    <div className="question-header">
                                      <span className="question-number">{`Q${idx + 1}:`}</span>
                                      <span className="question-text">
                                        {question.question}
                                        <span className="question-type-badge">
                                          {question.questionType}
                                        </span>
                                      </span>
                                      <div className="question-actions flex items-center gap-2">
                                        <div
                                          className="action-btn"
                                          onClick={() =>
                                            handleEditQuestion(question.id, {
                                              type: "large",
                                              category,
                                            })
                                          }
                                          style={{ cursor: "pointer" }}
                                        >
                                          <EditIcon />
                                        </div>
                                        <svg
                                          width="1"
                                          height="19"
                                          viewBox="0 0 1 19"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <line
                                            x1="0.5"
                                            x2="0.5"
                                            y2="19"
                                            stroke="#E5E5E5"
                                          />
                                        </svg>
                                        <div
                                          className="action-btn"
                                          onClick={() =>
                                            handleDeleteQuestion(question.id, {
                                              type: "large",
                                              category,
                                            })
                                          }
                                          style={{ cursor: "pointer" }}
                                        >
                                          <DeleteIcon />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          )}

                          {expandedCategories[category] && (
                            <div className="add-custom-question-container">
                              <div className="custom-question-header">
                                <div className="plus-icon" />
                                <h4>Add Custom Question</h4>
                              </div>
                              <div className="custom-question-content">
                                <p>
                                  Evaluate the consistency and frequency of
                                  executive-level touchpoints
                                </p>
                                <p>Ans: 1) Yes 2) No</p>
                              </div>
                              {renderCustomQuestionForm({
                                type: "large",
                                category,
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 3 - Preview */}
          {currentStep === 3 && (
            <div className="form-section preview-section">
              <div className="preview-main-card">
                <div className="assessment-title-container">
                  <div className="assessment-title-section">
                    <h2 className="assessment-title">
                      {formData.templateName} | Preview
                    </h2>
                    <p className="assessment-description">
                      {formData.description}
                    </p>
                  </div>
                </div>
                <div className="summary-stats-container">
                  <div className="stat-card">
                    <div className="stat-value">
                      {selectedCategories.length}
                    </div>
                    <div className="stat-label">Categories</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">
                      {selectedQuestionnaireType === "short"
                        ? smallAssessmentQuestionCount
                        : largeAssessmentQuestionCount}
                    </div>
                    <div className="stat-label">Questions</div>
                  </div>
                </div>
                <div className="assessment-categories-section">
                  <h3 className="categories-title">Assessment Categories</h3>
                  <div className="category-tags">
                    {selectedCategories.map(c => (
                      <div key={c} className="category-tag">
                        <span>{c}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="preview-assessment-questions-card">
                <div className="assessment-type-tabs">
                  <div className="flex gap-3 mb-4">
                    {[
                      {
                        id: "small",
                        label: "Short Assessment",
                      },
                      {
                        id: "large",
                        label: "Long Assessment",
                      },
                    ].map(item => (
                      <TabsComponent
                        key={item.id}
                        tabs={[item]}
                        activeTab={selectedQuestionnaireType}
                        onTabChange={setSelectedQuestionnaireType}
                        variant={
                          selectedQuestionnaireType === item.id
                            ? "pill-filled"
                            : "pill-outline"
                        }
                      />
                    ))}
                  </div>
                  <div className="assessment-meta-info">
                    <span className="questions-count">
                      {selectedQuestionnaireType === "short"
                        ? smallAssessmentQuestionCount
                        : largeAssessmentQuestionCount}{" "}
                      Questions
                    </span>
                    <span className="mandatory-count">2 mandatory</span>
                  </div>
                </div>

                <div className="assessment-separator-line" />

                <div className="preview-questions-section">
                  {selectedQuestionnaireType === "short" ? (
                    <>
                      {defaultCategoryQuestions.map((q, i) => (
                        <div
                          key={`preview-static-${i}`}
                          className="preview-question-item"
                        >
                          <div className="question-header">
                            <span className="question-number">{`Q${i + 1}:`}</span>
                            <span className="question-text">
                              {q.question}
                              <span className="question-type-badge">
                                {q.questionType}
                              </span>
                            </span>
                            {q.required && (
                              <div className="mandatory-badge">
                                <MandatoryIcon />
                                <span>Mandatory</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      {customQuestions.small.map((question, idx) => (
                        <div
                          key={question.id}
                          className="preview-question-item"
                        >
                          <div className="question-header">
                            <span className="question-number">{`Q${SMALL_STATIC_QUESTIONS_COUNT + idx + 1}:`}</span>
                            <span className="question-text">
                              {question.question}
                              <span className="question-type-badge">
                                {question.questionType}
                              </span>
                            </span>
                            {question.required && (
                              <div className="mandatory-badge">
                                <MandatoryIcon />
                                <span>Mandatory</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    selectedCategories
                      .flatMap(category => {
                        const customs = (
                          customQuestions.large[category] ?? []
                        ).map(q => ({ ...q }));
                        return customs;
                      })
                      .map((q, i) => (
                        <div
                          key={`preview-large-${i}`}
                          className="preview-question-item"
                        >
                          <div className="question-header">
                            <span className="question-number">{`Q${i + 1}:`}</span>
                            <span className="question-text">
                              {q.question}
                              <span className="question-type-badge">
                                {q.questionType}
                              </span>
                            </span>
                            {q.required && (
                              <div className="mandatory-badge">
                                <MandatoryIcon />
                                <span>Mandatory</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Footer Actions */}
          <div className="form-actions mt-4 flex justify-end gap-2">
            <Button variant="outline-primary" onClick={handleBack}>
              Back
            </Button>
            {currentStep < 3 ? (
              <Button variant="primary" onClick={handleNext}>
                Next
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                Create Template
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAssessmentTemplateContainer;
